// S28: Reset Password Page
import { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Alert,
  Space,
  Progress,
} from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // S37
import { updateLanguage } from '../i18n'; // S37
import {
  validateResetToken,
  resetPassword,
  ValidateTokenResponse,
} from '../api/passwordEndpoints';

const { Title, Text, Paragraph } = Typography;

interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
}

const calculatePasswordStrength = (password: string): PasswordStrength => {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  // Normalize to 0-4 scale
  const normalizedScore = Math.min(Math.floor(score / 1.5), 4);

  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['#ff4d4f', '#ff7a45', '#ffa940', '#52c41a', '#389e0d'];

  return {
    score: normalizedScore,
    label: labels[normalizedScore],
    color: colors[normalizedScore],
  };
};

const ResetPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { t } = useTranslation('auth'); // S37

  const [validating, setValidating] = useState(true);
  const [tokenData, setTokenData] = useState<ValidateTokenResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    label: 'Very Weak',
    color: '#ff4d4f',
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Reset token is missing');
        setValidating(false);
        return;
      }

      try {
        const response = await validateResetToken(token);
        if (response.valid) {
          setTokenData(response);
          setError(null); // Clear any previous errors
          // S37: Set language based on user's locale from token
          if (response.locale_bootstrap?.locale) {
            updateLanguage(response.locale_bootstrap.locale);
          }
        } else {
          setError(response.message);
        }
      } catch (err: any) {
        const message = err.response?.data?.message || 'Invalid or expired reset link';
        setError(message);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  useEffect(() => {
    if (password) {
      setPasswordStrength(calculatePasswordStrength(password));
    }
  }, [password]);

  const handleSubmit = async (values: {
    password: string;
    password_confirmation: string;
  }) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await resetPassword({
        token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      });

      // Store new JWT token
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      setSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      const message =
        err.response?.data?.error || 'Failed to reset password. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '24px',
          background: '#000000',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '48px',
            border: '1px solid #2a2a2a',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Title level={4} style={{ color: '#ffffff' }}>Validating reset link...</Title>
          </div>
        </div>
      </div>
    );
  }

  if (error && !tokenData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '24px',
          background: '#000000',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '48px',
            border: '1px solid #2a2a2a',
          }}
        >
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Alert title={error} type="error" showIcon />
            <Paragraph style={{ color: '#a0a0a0' }}>
              Your reset link may have expired or already been used. Please
              request a new password reset link.
            </Paragraph>
            <Button
              type="primary"
              block
              size="large"
              onClick={() => navigate('/forgot-password')}
            >
              Request New Link
            </Button>
            <div style={{ textAlign: 'center' }}>
              <Button type="link" onClick={() => navigate('/login')} style={{ color: '#ffffff' }}>
                Return to Login
              </Button>
            </div>
          </Space>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '24px',
          background: '#000000',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '48px',
            border: '1px solid #2a2a2a',
          }}
        >
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <CheckCircleOutlined
                style={{ fontSize: 64, color: '#52c41a' }}
              />
            </div>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ color: '#ffffff' }}>Password reset successful!</Title>
              <Paragraph style={{ color: '#a0a0a0' }}>
                Your password has been updated. Redirecting to dashboard...
              </Paragraph>
            </div>
          </Space>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '24px',
        background: '#000000',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '48px',
          border: '1px solid #2a2a2a',
        }}
      >
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ color: '#ffffff', marginBottom: '8px' }}>Reset your password</Title>
            {tokenData?.email && (
              <Text style={{ color: '#a0a0a0' }}>for {tokenData.email}</Text>
            )}
          </div>

          {error && <Alert title={error} type="error" showIcon closable />}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              label="New Password"
              name="password"
              rules={[
                { required: true, message: 'Please enter a new password' },
                { min: 8, message: 'Password must be at least 8 characters' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();

                    let complexity = 0;
                    if (/[a-z]/.test(value)) complexity++;
                    if (/[A-Z]/.test(value)) complexity++;
                    if (/[0-9]/.test(value)) complexity++;
                    if (/[^a-zA-Z0-9]/.test(value)) complexity++;

                    if (complexity < 3) {
                      return Promise.reject(
                        new Error(
                          'Password must contain at least 3 of: lowercase, uppercase, digit, special character'
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter new password"
                size="large"
                autoFocus
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Item>

            {password && (
              <div style={{ marginBottom: 24 }}>
                <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Password strength: {passwordStrength.label}
                  </Text>
                  <Progress
                    percent={(passwordStrength.score / 4) * 100}
                    strokeColor={passwordStrength.color}
                    showInfo={false}
                  />
                </Space>
              </div>
            )}

            <Form.Item
              label="Confirm New Password"
              name="password_confirmation"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Button type="link" onClick={() => navigate('/login')} style={{ color: '#ffffff' }}>
              Cancel
            </Button>
          </div>
        </Space>
      </div>
    </div>
  );
};

export default ResetPassword;
